import { Registry, type Identified } from './Registry';
import type { WaterKind } from './fish';

/**
 * Biyom tanımları.
 * Dünya üretimi sıcaklık/nem haritasından biyom seçer; görsel, kaynak,
 * canlı ve balıkçılık davranışı tamamen buradaki veriden gelir.
 */

export interface SpawnEntry {
  creature: string;
  weight: number;
  /** Sadece gece mi? */
  night?: boolean;
}

export interface BiomeDef extends Identified {
  name: string;
  /** Sıcaklık ve nem aralığı (0..1). Seçim bu aralıklara en yakın biyomu bulur. */
  temp: [number, number];
  moist: [number, number];
  /** Zemin ve sis renkleri. */
  ground: number;
  ground2: number;
  fog: number;
  fogDensity: number;
  sky: number;
  /** Yükseklik ölçeği çarpanı. */
  heightScale: number;
  /** m² başına yoğunluklar (0..1). */
  trees: number;
  rocks: number;
  grass: number;
  water: WaterKind;
  spawns: SpawnEntry[];
  /** Bu biyomda ağırlığı artan madenler. */
  oreBias?: Record<string, number>;
  /** Bölgede sürekli uygulanan durum efekti. */
  hazard?: { effect: string; magnitude: number };
  /** Nadir biyom mu? (üretimde daha seyrek görünür) */
  rare?: boolean;
  ambient?: string;
}

export const biomes = new Registry<BiomeDef>('biomes');

biomes.register(
  {
    id: 'plains',
    name: 'Ova',
    temp: [0.4, 0.75],
    moist: [0.25, 0.6],
    ground: 0x6aa84f,
    ground2: 0x86bd5f,
    fog: 0xbfd8e8,
    fogDensity: 0.0016,
    sky: 0x8ec5e8,
    heightScale: 0.55,
    trees: 0.1,
    rocks: 0.07,
    grass: 1,
    water: 'lake',
    spawns: [
      { creature: 'rabbit', weight: 40 },
      { creature: 'chicken', weight: 30 },
      { creature: 'cow', weight: 25 },
      { creature: 'sheep', weight: 25 },
      { creature: 'wolf', weight: 8, night: true },
    ],
    ambient: 'plains',
  },
  {
    id: 'forest',
    name: 'Orman',
    temp: [0.35, 0.7],
    moist: [0.55, 0.9],
    ground: 0x3f7d3a,
    ground2: 0x2f6b31,
    fog: 0xa9c6b4,
    fogDensity: 0.0028,
    sky: 0x87b8d8,
    heightScale: 0.8,
    trees: 0.85,
    rocks: 0.12,
    grass: 0.8,
    water: 'river',
    spawns: [
      { creature: 'deer', weight: 35 },
      { creature: 'rabbit', weight: 25 },
      { creature: 'boar', weight: 18 },
      { creature: 'wolf', weight: 22, night: true },
    ],
    ambient: 'forest',
  },
  {
    id: 'mountain',
    name: 'Dağ',
    temp: [0.15, 0.5],
    moist: [0.1, 0.5],
    ground: 0x8a8a8a,
    ground2: 0x6f6f6f,
    fog: 0xc4ccd4,
    fogDensity: 0.0014,
    sky: 0x9dc4e0,
    heightScale: 2.6,
    trees: 0.12,
    rocks: 0.7,
    grass: 0.2,
    water: 'lake',
    spawns: [
      { creature: 'goat', weight: 35 },
      { creature: 'wolf', weight: 20, night: true },
    ],
    oreBias: { iron: 2, coal: 2, silver: 1.8, gold: 1.5, diamond: 1.4, obsidian: 1.6 },
    ambient: 'wind',
  },
  {
    id: 'beach',
    name: 'Sahil',
    temp: [0.55, 0.95],
    moist: [0.3, 0.75],
    ground: 0xe6d6a2,
    ground2: 0xd9c68c,
    fog: 0xdcf0f7,
    fogDensity: 0.0012,
    sky: 0x9fd8f0,
    heightScale: 0.18,
    trees: 0.05,
    rocks: 0.05,
    grass: 0.1,
    water: 'sea',
    spawns: [
      { creature: 'seal', weight: 22 },
      { creature: 'crab', weight: 35 },
      { creature: 'chicken', weight: 12 },
    ],
    ambient: 'waves',
  },
  {
    id: 'swamp',
    name: 'Bataklık',
    temp: [0.5, 0.85],
    moist: [0.8, 1],
    ground: 0x4b5b39,
    ground2: 0x3a4a2c,
    fog: 0x7d8f74,
    fogDensity: 0.006,
    sky: 0x8f9c8a,
    heightScale: 0.3,
    trees: 0.35,
    rocks: 0.05,
    grass: 0.7,
    water: 'swamp',
    spawns: [
      { creature: 'frog', weight: 40 },
      { creature: 'swamp_lurker', weight: 18, night: true },
      { creature: 'boar', weight: 12 },
    ],
    hazard: { effect: 'slow', magnitude: 0.25 },
    ambient: 'swamp',
  },
  {
    id: 'ice',
    name: 'Buz',
    temp: [0, 0.2],
    moist: [0.2, 0.8],
    ground: 0xe8f2f8,
    ground2: 0xc9dfeb,
    fog: 0xe8f4ff,
    fogDensity: 0.0035,
    sky: 0xbcd8ea,
    heightScale: 1.1,
    trees: 0.15,
    rocks: 0.25,
    grass: 0.05,
    water: 'ice',
    spawns: [
      { creature: 'ice_wolf', weight: 25, night: true },
      { creature: 'seal', weight: 20 },
      { creature: 'goat', weight: 15 },
    ],
    oreBias: { silver: 1.6, diamond: 1.3, voidsteel: 1.4 },
    hazard: { effect: 'cold', magnitude: 0.2 },
    ambient: 'wind',
  },
  {
    id: 'venom',
    name: 'Mor Zehirli Bölge',
    temp: [0.45, 0.8],
    moist: [0.45, 0.85],
    ground: 0x4a2a63,
    ground2: 0x351e4d,
    fog: 0x8b3fd0,
    fogDensity: 0.009,
    sky: 0x5b2a7a,
    heightScale: 0.7,
    trees: 0.5,
    rocks: 0.2,
    grass: 0.6,
    water: 'venom',
    rare: true,
    spawns: [
      { creature: 'venom_crawler', weight: 40 },
      { creature: 'venom_stalker', weight: 20, night: true },
      { creature: 'glow_moth', weight: 25 },
    ],
    oreBias: { venomcrystal: 6, voidsteel: 2, blackDiamond: 1.5 },
    hazard: { effect: 'venom', magnitude: 1 },
    ambient: 'venom',
  },
);

/** Sıcaklık/nem değerine en uygun biyomu seçer. */
export function pickBiome(temp: number, moist: number, rareRoll: number): BiomeDef {
  let best: BiomeDef | null = null;
  let bestScore = Infinity;
  for (const b of biomes.all()) {
    // Nadir biyom yalnızca düşük rareRoll değerinde aday olur.
    if (b.rare && rareRoll > 0.04) continue;
    const tc = Math.max(b.temp[0], Math.min(b.temp[1], temp));
    const mc = Math.max(b.moist[0], Math.min(b.moist[1], moist));
    const score = (tc - temp) * (tc - temp) + (mc - moist) * (mc - moist) + (b.rare ? -0.05 : 0);
    if (score < bestScore) {
      bestScore = score;
      best = b;
    }
  }
  return best ?? biomes.require('plains');
}
